import React from 'react';
import CellTextInput from './CellTextInput';
import CellDatePicker from './CellDatePicker';
import CellBooleanInput from './CellBooleanInput';
import CellDropdown from './CellDropdown';
import CellNumberInput from './CellNumberInput';

import { Columns, DataGridRow, RowStatus } from 'components/DataGrid';
import { BoundingBox, FieldGroupColumn } from 'models/response';
import { UserDate, UserDecimalSep } from 'models/user';
import store from 'state/store';
import actions from 'state/actions';
import CellDialogInput from './CellDialogInput';

const onFocus = (cell?: { pageNumber: number; boundingBox: BoundingBox; content: string }) => {
  try {
    store.dispatch(actions.documents.setActiveBox(null));
    if (cell) {
      const { pageNumber, boundingBox } = cell;
      const { x1, x2, y1, y2 } = boundingBox;
      if (x1 > 0 && x2 > 0 && y1 > 0 && y2 > 0 && pageNumber > -1) {
        store.dispatch(
          actions.documents.setActiveBox({
            pageNumber,
            x1,
            y1,
            x2,
            y2,
          }),
        );
      }
    }
  } catch (err) {
    console.error(err);
  }
};

// Returns a custom width for col.dataType and col.type, if defined
function getColumnDefaultWidth(col: FieldGroupColumn) {
  switch (col.dataType) {
    case 0:
      switch (col.type) {
        case 1: // date
          return 120;
        case 2: // number
          return 120;
        case 3: // currency
          return 120;
        case 4: // boolean
          return 60;
        case 21: // text disabled
          return 200;
        default:
          return undefined;
      }
    case 3: // dropdown
      switch (col.type) {
        case 0: // dropdown
          return 200;
        case 21: // dropdown disabled
          return 200;
        default:
          return undefined;
      }
    // other custom dataType
    default:
      return undefined;
  }
}

const makeDataGridColumns = (
  cols: FieldGroupColumn[],
  name: string,
  userPreference: { decimalSep: UserDecimalSep; dateFormat: UserDate },
  rows: DataGridRow[],
  idCompany: number,
  idDocType: number,
  gridRef?: any,
  initialTextWidths?: { [key: string]: number },
  readOnly?: boolean,
) => {
  const idColumn: Columns = {
    title: 'ID',
    type: 'text',
    value: (row: DataGridRow) => {
      const rowIndex = rows.indexOf(row);
      return <span>{rowIndex + 1}</span>;
    },
    id: 'id',
    width: 50,
  };

  const generatedColumns = cols?.map((col): Columns => {
    let width;
    let maxWidth;
    const customWidth = getColumnDefaultWidth(col);
    if (customWidth !== undefined) {
      width = customWidth;
    } else if (col.dataType === 0 && col.type === 0 && initialTextWidths) {
      width = initialTextWidths[col.code];
      maxWidth = 400;
    }

    // Disable resize for text columns
    const isResizable = !(col.dataType === 0 && col.type === 0);
    return {
      title: col.translate || col.title,
      type: col.type === 1 ? 'date' : col.type === 3 ? 'number' : 'text',
      value: (row: DataGridRow, { focus }: RowStatus) => {
        switch (col.dataType) {
          case 0:
            switch (col.type) {
              case 0: // text
                return (
                  <CellTextInput
                    name={name}
                    focus={focus}
                    onFocus={onFocus}
                    row={row}
                    code={col.code}
                    disabled={readOnly}
                  />
                );
              case 1: // date
                return (
                  <CellDatePicker
                    dateFormat={userPreference.dateFormat}
                    name={name}
                    focus={focus}
                    onFocus={onFocus}
                    row={row}
                    code={col.code}
                    disabled={readOnly}
                  />
                );
              case 2: // number
                return (
                  <CellNumberInput
                    decimalSeparator={false}
                    name={name}
                    focus={focus}
                    onFocus={onFocus}
                    row={row}
                    code={col.code}
                    disabled={readOnly}
                  />
                );
              case 3: // currency
                return (
                  <CellNumberInput
                    decimalSeparator={userPreference.decimalSep || false}
                    hasFixedDecimals={true}
                    name={name}
                    focus={focus}
                    onFocus={onFocus}
                    row={row}
                    code={col.code}
                    disabled={readOnly}
                  />
                );
              case 4: // boolean
                return <CellBooleanInput name={name} focus={focus} row={row} code={col.code} disabled={readOnly} />;
              case 21: // disabled
                return <CellTextInput name={name} focus={focus} onFocus={onFocus} row={row} code={col.code} disabled />;

              default:
                return null;
            }
          case 3:
            switch (col.type) {
              case 0:
                return (
                  <CellDropdown
                    options={col.hTableValues}
                    name={name}
                    focus={focus}
                    onFocus={onFocus}
                    row={row}
                    code={col.code}
                    gridRef={gridRef}
                    disabled={readOnly}
                    key={col.key}
                  />
                );
              case 21: // dropdown disabled
                return (
                  <CellDropdown
                    options={col.hTableValues}
                    name={name}
                    focus={focus}
                    onFocus={onFocus}
                    row={row}
                    code={col.code}
                    gridRef={gridRef}
                    key={col.key}
                    disabled
                  />
                );
              default:
                return null;
            }
          case 8:
          case 10:
            return (
              <CellDialogInput
                name={name}
                focus={focus}
                onFocus={onFocus}
                row={row}
                code={col.code}
                gridRef={gridRef}
                disabled={readOnly}
                key={col.key}
                dialogTextInput={col.dataType === 10}
                link={col.link}
                idCompany={idCompany}
                idDocType={idDocType}
                hTableValues={col.hTableValues}
              />
            );
          default:
            return null;
        }
      },
      id: col.code,
      dateFormat: userPreference.dateFormat,
      userSeparator: userPreference.decimalSep,
      ...(width ? { width } : {}),
      ...(maxWidth ? { maxWidth } : {}),
      isResizable,
    };
  });

  return [idColumn, ...(generatedColumns || [])];
};

export default makeDataGridColumns;
